/**
 * Dialer Quick Actions row (Phase A).
 *
 * Horizontal scrollable action chips above the keypad — the fintech app
 * pattern (WhatsApp/Revolut "Actions" row).
 *
 * Live actions navigate; "Coming soon" actions surface a polite toast so the
 * roadmap stays visible to the user without cluttering the surface area.
 */
import { useNavigate } from "react-router-dom";
import {
  Phone, MessageSquare, Video, Users, Clock, Globe, Star, Image as ImageIcon, Mic,
} from "lucide-react";
import { toast } from "sonner";

const COMING_SOON_MSG = "Coming soon — landing in the next OneDial update.";

export default function DialerQuickActions({ onCall, onMessages, onContacts, onRecents, onFavorites }) {
  const nav = useNavigate();

  const actions = [
    { key: "call", label: "Call", icon: Phone, accent: "green",  testId: "qa-call",     onClick: onCall },
    { key: "msg",  label: "Messages", icon: MessageSquare, accent: "sky", testId: "qa-messages", onClick: () => (onMessages ? onMessages() : nav("/app/messages")) },
    { key: "vid",  label: "Video", icon: Video, accent: "violet", testId: "qa-video",
      comingSoon: true },
    { key: "cts",  label: "Contacts", icon: Users, accent: "indigo", testId: "qa-contacts", onClick: () => (onContacts ? onContacts() : nav("/app/contacts")) },
    { key: "rct",  label: "Recents",  icon: Clock, accent: "slate",  testId: "qa-recents", onClick: () => (onRecents ? onRecents() : nav("/app/calls")) },
    { key: "rate", label: "Rates",    icon: Globe, accent: "amber",  testId: "qa-rates",
      onClick: () => nav("/rates") },
    { key: "fav",  label: "Favorites", icon: Star, accent: "yellow", testId: "qa-favorites",
      onClick: () => (onFavorites ? onFavorites() : nav("/app/contacts?favorite=true")) },
    { key: "med",  label: "Media",   icon: ImageIcon, accent: "rose",   testId: "qa-media",   comingSoon: true },
    { key: "vn",   label: "Voice notes", icon: Mic,  accent: "emerald", testId: "qa-voicenotes", comingSoon: true },
  ];

  return (
    <div
      className="-mx-1 px-1 overflow-x-auto min-w-0 max-w-full"
      data-testid="dialer-quick-actions"
      style={{ scrollbarWidth: "none" }}
    >
      <ul className="flex items-stretch gap-2 snap-x snap-mandatory pb-1">
        {actions.map((a) => (
          <li key={a.key} className="snap-start">
            <button
              type="button"
              onClick={() => (a.comingSoon ? toast.info(`${a.label}: ${COMING_SOON_MSG}`) : a.onClick?.())}
              data-testid={a.testId}
              aria-label={a.label}
              className="group relative shrink-0 w-[78px] flex flex-col items-center gap-1.5 rounded-2xl bg-white border border-brand-line/80 hover:border-brand-green/50 active:scale-[0.97] transition px-2 py-2.5"
            >
              <span className={`relative inline-flex items-center justify-center h-11 w-11 rounded-2xl ${accentBg(a.accent)} ${accentText(a.accent)} shadow-[inset_0_-2px_0_rgba(0,0,0,0.04)]`}>
                <a.icon className="h-5 w-5" strokeWidth={2.1} />
                {a.comingSoon && (
                  <span className="absolute -top-1 -right-1 rounded-full bg-amber-400 text-amber-900 text-[8px] font-black px-1 leading-[12px] ring-2 ring-white" data-testid={`${a.testId}-soon`}>
                    SOON
                  </span>
                )}
              </span>
              <span className="text-[10px] font-bold text-brand-ink/80 leading-tight text-center">{a.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function accentBg(name) {
  switch (name) {
    case "green":   return "bg-emerald-50";
    case "sky":     return "bg-sky-50";
    case "violet":  return "bg-violet-50";
    case "indigo":  return "bg-indigo-50";
    case "slate":   return "bg-slate-100";
    case "amber":   return "bg-amber-50";
    case "yellow":  return "bg-yellow-50";
    case "rose":    return "bg-rose-50";
    case "emerald": return "bg-emerald-50";
    default:        return "bg-brand-bgSoft";
  }
}
function accentText(name) {
  switch (name) {
    case "green":   return "text-emerald-600";
    case "sky":     return "text-sky-600";
    case "violet":  return "text-violet-600";
    case "indigo":  return "text-indigo-600";
    case "slate":   return "text-slate-600";
    case "amber":   return "text-amber-600";
    case "yellow":  return "text-yellow-600";
    case "rose":    return "text-rose-600";
    case "emerald": return "text-emerald-600";
    default:        return "text-brand-ink";
  }
}
